import React, { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { motion } from "framer-motion";
import { FaEye, FaEyeSlash } from "react-icons/fa";

const CSORegistration = () => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    csoName: "",
    repName: "",
    email: "",
    phone: "",
    sector: "",
    location: "",
    office: "",
    logo: null,
    tin_certificate: null,
    registration_certificate: null,
    official_rep_letter: null,
    status: "active",
    role: "cso",
    password: "",
    confirmPassword: "",
    registrationId: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [filePreviews, setFilePreviews] = useState({
    logo: null,
    tin_certificate: null,
    registration_certificate: null,
    official_rep_letter: null,
  });

  // password visibility states
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    if (!files || !files[0]) return;

    const file = files[0];

    // limit file size to 5MB
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error("File size should be less than 5MB");
      return;
    }

    // set file into formData
    setFormData((prev) => ({ ...prev, [name]: file }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));

    // preview for images (logo or image docs)
    if (file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setFilePreviews((prev) => ({ ...prev, [name]: event.target.result }));
      };
      reader.readAsDataURL(file);
    } else {
      // non-image docs: clear preview but mark selected
      setFilePreviews((prev) => ({ ...prev, [name]: null }));
    }
  };

  const isValidPhoneNumber = (phone) => {
    // +2519XXXXXXXX or 09XXXXXXXX
    const ethioFormat = /^\+2519\d{8}$/;
    const localFormat = /^09\d{8}$/;
    return ethioFormat.test(phone) || localFormat.test(phone);
  };

  const validateStep1 = () => {
    const newErrors = {};
    const requiredFields = [
      "csoName",
      "repName",
      "email",
      "phone",
      "sector",
      "location",
      "office",
      "logo",
      "tin_certificate",
      "registration_certificate",
      "official_rep_letter",
    ];

    requiredFields.forEach((field) => {
      if (!formData[field]) {
        newErrors[field] = `${field.split("_").join(" ")} is required`;
      }
    });

    if (formData.email && !/^\S+@\S+\.\S+$/.test(formData.email)) {
      newErrors.email = "Email is invalid";
    }

    if (formData.phone && !isValidPhoneNumber(formData.phone)) {
      newErrors.phone =
        "Phone must be in +2519XXXXXXXX or 09XXXXXXXX format (10 or 13 digits)";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    const newErrors = {};
    const passwordRegex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;

    if (!formData.registrationId)
      newErrors.registrationId = "Registration ID is required";

    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (!passwordRegex.test(formData.password)) {
      newErrors.password =
        "Password must be at least 8 characters and include uppercase, lowercase, number, and special character";
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // check existing CSO by email or phone
  const checkExistingCSO = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/cso/email/check`,
        {
          params: { email: formData.email, phone: formData.phone },
        }
      );

      if (response.data?.success && response.data?.data?.length > 0) {
        return response.data.data[0].registrationId;
      }
      return null;
    } catch (error) {
      console.error("Error checking CSO:", error);
      // don't block user if check fails, just return null
      return null;
    }
  };

  // Step 1 submit: create CSO (or detect existing)
  const handleNext = async (e) => {
    e.preventDefault();
    if (!validateStep1()) return;

    setIsSubmitting(true);
    toast.info("Processing registration...");

    try {
      const existingRegistrationId = await checkExistingCSO();

      if (existingRegistrationId) {
        toast.info("CSO already registered. Please create your account.");
        setFormData((prev) => ({
          ...prev,
          registrationId: existingRegistrationId,
        }));
        setStep(2);
        return;
      }

      const payload = new FormData();
      const fields = [
        "csoName",
        "repName",
        "email",
        "phone",
        "sector",
        "location",
        "office",
        "status",
        "role",
      ];
      fields.forEach((f) => payload.append(f, formData[f] ?? ""));

      const fileFields = [
        "logo",
        "tin_certificate",
        "registration_certificate",
        "official_rep_letter",
      ];
      fileFields.forEach((f) => {
        if (formData[f]) payload.append(f, formData[f]);
      });

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/cso/register`,
        payload,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      if (response.data?.success) {
        toast.success("CSO registered successfully! Please create your account.");
        setFormData((prev) => ({
          ...prev,
          registrationId: response.data.registrationId || prev.registrationId,
        }));
        setStep(2);
      } else {
        throw new Error(response.data?.message || "Registration failed");
      }
    } catch (error) {
      console.error("Registration error:", error);
      toast.error(
        error.response?.data?.message || error.message || "Registration failed. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    setStep(1);
  };

  // Step 2 submit: create user account
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep2()) return;

    setIsSubmitting(true);
    toast.info("Creating account...");

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/users/createAccount`,
        {
          registrationId: formData.registrationId,
          password: formData.password,
        }
      );

      if (response.data?.success) {
        toast.success("Account created successfully! You can now login.");
        // navigate after slight delay to let toast appear
        setTimeout(() => navigate("/user/login"), 1200);
      } else {
        throw new Error(response.data?.message || "Account creation failed");
      }
    } catch (error) {
      console.error("Error creating account:", error);
      toast.error(
        error.response?.data?.message ||
          error.message ||
          "Failed to create account. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // helper to request server to register/create and return registrationId when clicking refresh
  const fetchRegistrationId = async () => {
    setIsSubmitting(true);
    toast.info("Generating registration ID...");

    try {
      const existingRegistrationId = await checkExistingCSO();
      if (existingRegistrationId) {
        setFormData((prev) => ({ ...prev, registrationId: existingRegistrationId }));
        toast.info("Using existing registration ID");
        return;
      }

      const payload = new FormData();
      const fields = [
        "csoName",
        "repName",
        "email",
        "phone",
        "sector",
        "location",
        "office",
        "status",
        "role",
      ];
      fields.forEach((f) => payload.append(f, formData[f] ?? ""));

      const fileFields = [
        "logo",
        "tin_certificate",
        "registration_certificate",
        "official_rep_letter",
      ];
      fileFields.forEach((f) => {
        if (formData[f]) payload.append(f, formData[f]);
      });

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/cso/register`,
        payload,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      if (response.data?.success) {
        setFormData((prev) => ({
          ...prev,
          registrationId: response.data.registrationId || prev.registrationId,
        }));
        toast.success("Registration ID generated successfully!");
      } else {
        throw new Error(response.data?.message || "Registration failed");
      }
    } catch (error) {
      console.error("Registration error:", error);
      toast.error(error.response?.data?.message || "Failed to get registration ID");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRefreshRegistrationId = async (e) => {
    e.preventDefault();
    await fetchRegistrationId();
  };

  return (
    <div className="min-h-screen pt-8 flex items-center justify-center bg-gradient-to-br from-indigo-50 to-white">
      <ToastContainer position="top-right" autoClose={5000} />

      <div className="w-full max-w-6xl mx-4 my-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45 }}
          className="bg-white shadow-xl rounded-2xl overflow-hidden"
        >
          <div className="bg-gradient-to-r from-indigo-600 to-indigo-800 p-6 text-white">
            <div className="flex items-center mb-4">
              <img
                src="/logo3.png"
                alt="Company Logo"
                className="h-12 w-12 mr-4 bg-white rounded-full p-1"
              />
              <h1 className="text-2xl font-bold">Bishoftu Finance Office</h1>
            </div>
            <p>This Registration Page is for CSO Users Only</p>

            <div className="flex flex-col sm:flex-row justify-between items-center mt-4">
              <h2 className="text-xl font-semibold mb-4 sm:mb-0">
                {step === 1 ? "CSO Registration" : "Create Your Account"}
              </h2>

              <div className="flex items-center space-x-4">
                <div className={`flex items-center ${step === 1 ? "text-white" : "text-indigo-200"}`}>
                  <div className={`flex items-center justify-center w-8 h-8 rounded-full ${step === 1 ? "bg-white text-indigo-600" : "bg-indigo-500"}`}>
                    1
                  </div>
                  <span className="ml-2 text-sm">Organization Info</span>
                </div>

                <div className="h-1 w-8 bg-indigo-400"></div>

                <div className={`flex items-center ${step === 2 ? "text-white" : "text-indigo-200"}`}>
                  <div className={`flex items-center justify-center w-8 h-8 rounded-full ${step === 2 ? "bg-white text-indigo-600" : "bg-indigo-500"}`}>
                    2
                  </div>
                  <span className="ml-2 text-sm">Account Setup</span>
                </div>
              </div>
            </div>
          </div>

          <div className="p-6 md:p-8">
            {step === 1 ? (
              <form onSubmit={handleNext} className="space-y-6">
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">CSO Name *</label>
                      <input
                        name="csoName"
                        value={formData.csoName}
                        onChange={handleChange}
                        className={`w-full px-4 py-3 rounded-lg border ${errors.csoName ? "border-red-500" : "border-gray-300"} focus:outline-none focus:ring-2 focus:ring-indigo-500 transition`}
                        placeholder="Enter organization name"
                      />
                      {errors.csoName && <p className="mt-1 text-sm text-red-600">{errors.csoName}</p>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Representative Name *</label>
                      <input
                        name="repName"
                        value={formData.repName}
                        onChange={handleChange}
                        className={`w-full px-4 py-3 rounded-lg border ${errors.repName ? "border-red-500" : "border-gray-300"} focus:outline-none focus:ring-2 focus:ring-indigo-500 transition`}
                        placeholder="Enter representative's name"
                      />
                      {errors.repName && <p className="mt-1 text-sm text-red-600">{errors.repName}</p>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                      <input
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        className={`w-full px-4 py-3 rounded-lg border ${errors.email ? "border-red-500" : "border-gray-300"} focus:outline-none focus:ring-2 focus:ring-indigo-500 transition`}
                        placeholder="Enter organization email"
                      />
                      {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
                      <input
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        className={`w-full px-4 py-3 rounded-lg border ${errors.phone ? "border-red-500" : "border-gray-300"} focus:outline-none focus:ring-2 focus:ring-indigo-500 transition`}
                        placeholder="Enter phone number (+2519XXXXXXXX or 09XXXXXXXX)"
                      />
                      {errors.phone && <p className="mt-1 text-sm text-red-600">{errors.phone}</p>}
                    </div>
                  </div>

                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Sector *</label>
                      <input
                        name="sector"
                        value={formData.sector}
                        onChange={handleChange}
                        className={`w-full px-4 py-3 rounded-lg border ${errors.sector ? "border-red-500" : "border-gray-300"} focus:outline-none focus:ring-2 focus:ring-indigo-500 transition`}
                        placeholder="Enter sector/industry"
                      />
                      {errors.sector && <p className="mt-1 text-sm text-red-600">{errors.sector}</p>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Location *</label>
                      <input
                        name="location"
                        value={formData.location}
                        onChange={handleChange}
                        className={`w-full px-4 py-3 rounded-lg border ${errors.location ? "border-red-500" : "border-gray-300"} focus:outline-none focus:ring-2 focus:ring-indigo-500 transition`}
                        placeholder="Enter physical address"
                      />
                      {errors.location && <p className="mt-1 text-sm text-red-600">{errors.location}</p>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Office *</label>
                      <input
                        name="office"
                        value={formData.office}
                        onChange={handleChange}
                        className={`w-full px-4 py-3 rounded-lg border ${errors.office ? "border-red-500" : "border-gray-300"} focus:outline-none focus:ring-2 focus:ring-indigo-500 transition`}
                        placeholder="Enter office location"
                      />
                      {errors.office && <p className="mt-1 text-sm text-red-600">{errors.office}</p>}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-6 mt-6 md:grid-cols-2">
                  {/* Logo */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Organization Logo *</label>
                    <div className="flex items-center">
                      <label className="flex flex-col items-center justify-center w-full p-4 border-2 border-dashed rounded-lg cursor-pointer hover:bg-gray-50 transition">
                        <div className="flex flex-col items-center justify-center">
                          <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                          </svg>
                          <p className="text-sm text-gray-500 mt-2">
                            {filePreviews.logo ? "Change logo" : "Upload logo (PNG, JPG)"}
                          </p>
                        </div>
                        <input
                          name="logo"
                          type="file"
                          accept="image/*"
                          onChange={handleFileChange}
                          className="hidden"
                        />
                      </label>
                    </div>
                    {errors.logo && <p className="mt-1 text-sm text-red-600">{errors.logo}</p>}
                    {filePreviews.logo && (
                      <div className="mt-2 flex items-center">
                        <img src={filePreviews.logo} alt="Logo preview" className="h-16 w-16 rounded-full object-cover border" />
                        <span className="ml-2 text-sm text-gray-500">Logo selected</span>
                      </div>
                    )}
                  </div>

                  {/* TIN */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">TIN Certificate *</label>
                    <div className="flex items-center">
                      <label className="flex flex-col items-center justify-center w-full p-4 border-2 border-dashed rounded-lg cursor-pointer hover:bg-gray-50 transition">
                        <div className="flex flex-col items-center justify-center">
                          <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"></path>
                          </svg>
                          <p className="text-sm text-gray-500 mt-2">
                            {filePreviews.tin_certificate ? "Change document" : "Upload TIN certificate (PDF, JPG, PNG)"}
                          </p>
                        </div>
                        <input
                          name="tin_certificate"
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={handleFileChange}
                          className="hidden"
                        />
                      </label>
                    </div>
                    {errors.tin_certificate && <p className="mt-1 text-sm text-red-600">{errors.tin_certificate}</p>}
                    {formData.tin_certificate && !filePreviews.tin_certificate && <div className="mt-2"><span className="text-sm text-gray-500">Document selected</span></div>}
                  </div>

                  {/* Registration cert */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Registration Certificate *</label>
                    <div className="flex items-center">
                      <label className="flex flex-col items-center justify-center w-full p-4 border-2 border-dashed rounded-lg cursor-pointer hover:bg-gray-50 transition">
                        <div className="flex flex-col items-center justify-center">
                          <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"></path>
                          </svg>
                          <p className="text-sm text-gray-500 mt-2">
                            {filePreviews.registration_certificate ? "Change document" : "Upload registration certificate (PDF, JPG, PNG)"}
                          </p>
                        </div>
                        <input
                          name="registration_certificate"
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={handleFileChange}
                          className="hidden"
                        />
                      </label>
                    </div>
                    {errors.registration_certificate && <p className="mt-1 text-sm text-red-600">{errors.registration_certificate}</p>}
                    {formData.registration_certificate && !filePreviews.registration_certificate && <div className="mt-2"><span className="text-sm text-gray-500">Document selected</span></div>}
                  </div>

                  {/* Representative letter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Representative Letter *</label>
                    <div className="flex items-center">
                      <label className="flex flex-col items-center justify-center w-full p-4 border-2 border-dashed rounded-lg cursor-pointer hover:bg-gray-50 transition">
                        <div className="flex flex-col items-center justify-center">
                          <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"></path>
                          </svg>
                          <p className="text-sm text-gray-500 mt-2">
                            {filePreviews.official_rep_letter ? "Change document" : "Upload representative letter (PDF, JPG, PNG)"}
                          </p>
                        </div>
                        <input
                          name="official_rep_letter"
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={handleFileChange}
                          className="hidden"
                        />
                      </label>
                    </div>
                    {errors.official_rep_letter && <p className="mt-1 text-sm text-red-600">{errors.official_rep_letter}</p>}
                    {formData.official_rep_letter && !filePreviews.official_rep_letter && <div className="mt-2"><span className="text-sm text-gray-500">Document selected</span></div>}
                  </div>
                </div>

                <div className="flex justify-end pt-6">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    type="submit"
                    disabled={isSubmitting}
                    className="px-6 py-3 rounded-lg font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 transition shadow-md"
                  >
                    {isSubmitting ? "Processing..." : "Continue to Account Setup"}
                  </motion.button>
                </div>
              </form>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="bg-indigo-50 p-6 rounded-xl">
                  <h4 className="text-lg font-semibold text-indigo-800 mb-4">Organization Summary</h4>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div>
                      <p className="text-sm text-indigo-600">CSO Name</p>
                      <p className="text-md font-medium text-gray-800">{formData.csoName}</p>
                    </div>
                    <div>
                      <p className="text-sm text-indigo-600">Representative</p>
                      <p className="text-md font-medium text-gray-800">{formData.repName}</p>
                    </div>
                    <div>
                      <p className="text-sm text-indigo-600">Email</p>
                      <p className="text-md font-medium text-gray-800">{formData.email}</p>
                    </div>
                    <div>
                      <p className="text-sm text-indigo-600">Phone</p>
                      <p className="text-md font-medium text-gray-800">{formData.phone}</p>
                    </div>
                  </div>
                </div>

                <div className="mt-6">
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Registration ID *</label>
                    <div className="flex">
                      <input
                        name="registrationId"
                        value={formData.registrationId}
                        onChange={handleChange}
                        className={`w-full px-4 py-3 rounded-l-lg border ${errors.registrationId ? "border-red-500" : "border-gray-300"} focus:outline-none focus:ring-2 focus:ring-indigo-500 transition`}
                        readOnly
                      />
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={handleRefreshRegistrationId}
                        disabled={isSubmitting}
                        className="px-4 py-3 bg-indigo-600 text-white rounded-r-lg hover:bg-indigo-700 disabled:opacity-50 transition"
                      >
                        {isSubmitting ? "..." : <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" /></svg>}
                      </motion.button>
                    </div>
                    {errors.registrationId && <p className="mt-1 text-sm text-red-600">{errors.registrationId}</p>}
                  </div>

                  <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Password *</label>
                      <div className="relative">
                        <input
                          name="password"
                          type={showPassword ? "text" : "password"}
                          value={formData.password}
                          onChange={handleChange}
                          className={`w-full px-4 py-3 rounded-lg border ${errors.password ? "border-red-500" : "border-gray-300"} focus:outline-none focus:ring-2 focus:ring-indigo-500 transition`}
                          disabled={!formData.registrationId}
                          placeholder="Create a password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword((s) => !s)}
                          aria-label={showPassword ? "Hide password" : "Show password"}
                          className="absolute right-3 top-3.5 text-gray-600"
                        >
                          {showPassword ? (
                            <FaEyeSlash className="h-5 w-5" />
                          ) : (
                            <FaEye className="h-5 w-5" />
                          )}
                        </button>
                      </div>
                      {errors.password && <p className="mt-1 text-sm text-red-600">{errors.password}</p>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password *</label>
                      <div className="relative">
                        <input
                          name="confirmPassword"
                          type={showConfirmPassword ? "text" : "password"}
                          value={formData.confirmPassword}
                          onChange={handleChange}
                          className={`w-full px-4 py-3 rounded-lg border ${errors.confirmPassword ? "border-red-500" : "border-gray-300"} focus:outline-none focus:ring-2 focus:ring-indigo-500 transition`}
                          disabled={!formData.registrationId}
                          placeholder="Confirm your password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword((s) => !s)}
                          aria-label={showConfirmPassword ? "Hide confirm password" : "Show confirm password"}
                          className="absolute right-3 top-3.5 text-gray-600"
                        >
                          {showConfirmPassword ? (
                            <FaEyeSlash className="h-5 w-5" />
                          ) : (
                            <FaEye className="h-5 w-5" />
                          )}
                        </button>
                      </div>
                      {errors.confirmPassword && <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>}
                    </div>
                  </div>
                </div>

                <div className="flex justify-between pt-6">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    type="button"
                    onClick={handleBack}
                    className="px-6 py-3 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition shadow-sm"
                  >
                    Back
                  </motion.button>

                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    type="submit"
                    disabled={isSubmitting || !formData.registrationId}
                    className="px-6 py-3 rounded-lg text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 transition shadow-lg"
                  >
                    {isSubmitting ? "Creating Account..." : "Complete Registration"}
                  </motion.button>
                </div>
              </form>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default CSORegistration;
